import { Injectable } from '@angular/core';
import { ISurvey, Survey } from '../models/survey.model';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { HttpClient } from '@angular/common/http';
import { SpinnerService } from './spinner.service';
import { ToastService } from './toast.service';
import { IServerResponse, ServerResponse } from '../models/server-response.model';
import { AwsService } from './aws.service';
import { IPresignResponse } from '../models/presign-response.model';
import { IUploadPosition } from '../models/upload-progress.model';

@Injectable({
  providedIn: 'root'
})
export class SurveyService {

  public loading: boolean = false;
  public surveys: Survey[] = [];

  constructor(
    public authService: AuthService,
    public awsService: AwsService,
    private http: HttpClient,
    public spinnerService: SpinnerService,
    public toastService: ToastService
  ) {}

  /*
   ██████╗██████╗ ███████╗ █████╗ ████████╗███████╗
  ██╔════╝██╔══██╗██╔════╝██╔══██╗╚══██╔══╝██╔════╝
  ██║     ██████╔╝█████╗  ███████║   ██║   █████╗
  ██║     ██╔══██╗██╔══╝  ██╔══██║   ██║   ██╔══╝
  ╚██████╗██║  ██║███████╗██║  ██║   ██║   ███████╗
   ╚═════╝╚═╝  ╚═╝╚══════╝╚═╝  ╚═╝   ╚═╝   ╚══════╝
  */
  async create(
    neighborhoodId: string,
    survey: ISurvey
  ): Promise<boolean> {

    let flag = false;

    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/surveys`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Registrando encuesta';

    try {
      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse>(
        (resolve, reject) => {
          this.http.post<IServerResponse>(
            urlApi,
            JSON.stringify(survey),
            {
              headers: {
                'content-type': 'application/json',
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (
              response: IServerResponse
            ) => resolve(new ServerResponse(response)),

            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
                this.toastService.show({
                  header: 'Error de servidor',
                  body: `${error}`,
                  classname: 'bg-danger text-white'
                });
              }
            }
          });
        }
      );

      if (response.statusCode === 201) {
        flag = true;
        this.toastService.show({
          body: `${response.message}`,
          classname: 'bg-success text-white'
        });
        this.getAll(neighborhoodId);
      } else {
        this.toastService.show({
          header: 'Error de servidor',
          body: `${response.message}`,
          classname: 'bg-danger text-white'
        });
      }

    } catch (error) {
      console.log(error);
      this.toastService.show({
        header: 'Error',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';
    return flag;
  }

  /*
  ██████╗ ███████╗██╗     ███████╗████████╗███████╗
  ██╔══██╗██╔════╝██║     ██╔════╝╚══██╔══╝██╔════╝
  ██║  ██║█████╗  ██║     █████╗     ██║   █████╗
  ██║  ██║██╔══╝  ██║     ██╔══╝     ██║   ██╔══╝
  ██████╔╝███████╗███████╗███████╗   ██║   ███████╗
  ╚═════╝ ╚══════╝╚══════╝╚══════╝   ╚═╝   ╚══════╝

  */
  async delete(
    survey: Survey
  ): Promise<boolean> {
  
    let flag = false;
  
    const urlApi = `${environment.urlApi}/neighborhoods/${survey.neighborhoodId}/surveys/${survey.surveyId}`;
  
    this.spinnerService.loading = true;
    this.spinnerService.message = 'Eliminando encuesta';
  
    try {
      const token = await this.authService.getToken();
  
      const response = await new Promise<ServerResponse>(
        (resolve, reject) => {
          this.http.delete<IServerResponse>(
            urlApi,
            {
              headers: {
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (
              response: IServerResponse
            ) => resolve(new ServerResponse(response)),
  
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
                this.toastService.show({
                  header: 'Error de servidor',
                  body: `${error}`,
                  classname: 'bg-danger text-white'
                });
              }
            }
          });
        }
      );
  
      if (response.statusCode === 200) {
        flag = true;
  
        this.toastService.show({
          body: `${response.message}`,
          classname: 'bg-success text-white'
        });
  
        this.getAll(survey.neighborhoodId);
      } else {
        this.toastService.show({
          header: 'Error de servidor',
          body: `${response.message}`,
          classname: 'bg-danger text-white'
        });
      }
  
    } catch (error) {
      console.log(error);
  
      this.toastService.show({
        header: 'Error',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });
    }
  
    this.spinnerService.loading = false;
    this.spinnerService.message = '';
    return flag;
  }

  /*
   ██████╗ ███████╗████████╗
  ██╔════╝ ██╔════╝╚══██╔══╝
  ██║  ███╗█████╗     ██║
  ██║   ██║██╔══╝     ██║
  ╚██████╔╝███████╗   ██║
   ╚═════╝ ╚══════╝   ╚═╝

  */
  async get(
    neighborhoodId: string,
    surveyId: string
  ): Promise<Survey | null> {
  
    let survey: Survey | null = null;
  
    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/surveys/${surveyId}`;
    this.spinnerService.loading = true;
    this.spinnerService.message = 'Obteniendo encuesta';
  
    try {
      const token = await this.authService.getToken();
  
      const response = await new Promise<ServerResponse<{ survey: ISurvey }>>(
        (resolve, reject) => {
          this.http.get<IServerResponse<{ survey: ISurvey }>>(
            urlApi,
            {
              headers: {
                'content-type': 'application/json',
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (
              response: IServerResponse<{ survey: ISurvey }>
            ) => resolve(
              new ServerResponse<{ survey: ISurvey }>(response)
            ),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
                this.toastService.show({
                  header: 'Error de servidor',
                  body: `${error}`,
                  classname: 'bg-danger text-white'
                });
              }
            }
          });
        }
      );

      console.log(response);
  
      if (response.statusCode === 200) {
        survey = new Survey(response.data!.survey);
      } else {
        this.toastService.show({
          header: 'Error de servidor',
          body: `${response.message}`,
          classname: 'bg-danger text-white'
        });
      }
  
    } catch (error) {
      console.log(error);
  
      this.toastService.show({
        header: 'Error',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });
    }
  
    this.spinnerService.loading = false;
    this.spinnerService.message = '';
    return survey;
  }
  
  /*
   ██████╗ ███████╗████████╗ █████╗ ██╗     ██╗
  ██╔════╝ ██╔════╝╚══██╔══╝██╔══██╗██║     ██║
  ██║  ███╗█████╗     ██║   ███████║██║     ██║
  ██║   ██║██╔══╝     ██║   ██╔══██║██║     ██║
  ╚██████╔╝███████╗   ██║   ██║  ██║███████╗███████╗
   ╚═════╝ ╚══════╝   ╚═╝   ╚═╝  ╚═╝╚══════╝╚══════╝

  */
  async getAll(
    neighborhoodId: string
  ) {
    this.loading = true;
    this.surveys = [];
  
    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/surveys`;
  
    try {
      const token = await this.authService.getToken();
  
      const response = await new Promise<ServerResponse<{ surveys: ISurvey[] }>>(
        (resolve, reject) => {
          this.http.get<IServerResponse<{ surveys: ISurvey[] }>>(
            urlApi,
            {
              headers: {
                'content-type': 'application/json',
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (
              response: IServerResponse<{ surveys: ISurvey[] }>
            ) => resolve(
              new ServerResponse<{ surveys: ISurvey[] }>(response)
            ),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
                this.toastService.show({
                  header: 'Error de servidor',
                  body: `${error}`,
                  classname: 'bg-danger text-white'
                });
              }
            }
          });
        }
      );
  
      if (response.statusCode === 200) {
        this.surveys = response.data!.surveys.map(
          survey => new Survey(survey)
        );
      } else {
        this.toastService.show({
          header: 'Error de servidor',
          body: `${response.message}`,
          classname: 'bg-danger text-white'
        });
      }
  
    } catch (error) {
      console.log(error);
  
      this.toastService.show({
        header: 'Error',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });
    }
  
    this.loading = false;
  }

  /*
  ██╗   ██╗██████╗ ██████╗  █████╗ ████████╗███████╗
  ██║   ██║██╔══██╗██╔══██╗██╔══██╗╚══██╔══╝██╔════╝
  ██║   ██║██████╔╝██║  ██║███████║   ██║   █████╗
  ██║   ██║██╔═══╝ ██║  ██║██╔══██║   ██║   ██╔══╝
  ╚██████╔╝██║     ██████╔╝██║  ██║   ██║   ███████╗
   ╚═════╝ ╚═╝     ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚══════╝

  */
  async update(
    survey: Survey
  ): Promise<boolean> {
  
    let flag = false;
  
    const urlApi = `${environment.urlApi}/neighborhoods/${survey.neighborhoodId}/surveys/${survey.surveyId}`;
    this.spinnerService.loading = true;
    this.spinnerService.message = 'Actualizando encuesta';
  
    try {
      const token = await this.authService.getToken();
  
      const response = await new Promise<ServerResponse>(
        (resolve, reject) => {
          this.http.put<IServerResponse>(
            urlApi,
            JSON.stringify(survey),
            {
              headers: {
                'content-type': 'application/json',
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (
              response: IServerResponse
            ) => resolve(
              new ServerResponse(response)
            ),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
                this.toastService.show({
                  header: 'Error de servidor',
                  body: `${error}`,
                  classname: 'bg-danger text-white'
                });
              }
            }
          });
        }
      );
  
      if (response.statusCode === 200) {
        flag = true;
  
        this.toastService.show({
          body: `${response.message}`,
          classname: 'bg-success text-white'
        });
  
        this.getAll(survey.neighborhoodId);
  
      } else {
        this.toastService.show({
          header: 'Error de servidor',
          body: `${response.message}`,
          classname: 'bg-danger text-white'
        });
      }
  
    } catch (error) {
      console.log(error);
  
      this.toastService.show({
        header: 'Error',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });
    }
  
    this.spinnerService.loading = false;
    this.spinnerService.message = '';
    return flag;
  }
  
  /*
    ██╗   ██╗██████╗ ██╗      ██████╗  █████╗ ██████╗ ███████╗██╗██╗     ███████╗
    ██║   ██║██╔══██╗██║     ██╔═══██╗██╔══██╗██╔══██╗██╔════╝██║██║     ██╔════╝
    ██║   ██║██████╔╝██║     ██║   ██║███████║██║  ██║█████╗  ██║██║     █████╗
    ██║   ██║██╔═══╝ ██║     ██║   ██║██╔══██║██║  ██║██╔══╝  ██║██║     ██╔══╝
    ╚██████╔╝██║     ███████╗╚██████╔╝██║  ██║██████╔╝██║     ██║███████╗███████╗
     ╚═════╝ ╚═╝     ╚══════╝ ╚═════╝ ╚═╝  ╚═╝╚═════╝ ╚═╝     ╚═╝╚══════╝╚══════╝
  
    */
    async uploadFile(
      survey: Survey,
      base64Data: string,
      fileCategory: string,
      fileName?: string,
      position: IUploadPosition | null = null
    ): Promise<boolean> {

      let flag = false;

      const name = fileName ?? (
        fileCategory === 'pdf' ? 'Documento PDF' : 'Imagen'
      );

      const urlApi = `${environment.urlApi}/neighborhoods/${survey.neighborhoodId}/surveys/${survey.surveyId}/files`;

      // Extraer el MIME type real del data URI (ej: "data:image/png;base64,..." -> "image/png")
      const mimeMatch = base64Data.match(/^data:([^;]+);base64,/);
      const contentType = mimeMatch ? mimeMatch[1] : (fileCategory === 'pdf' ? 'application/pdf' : 'image/jpeg');

      // Convertir base64 a Blob para subir a S3
      const byteString = atob(base64Data.split(',')[1]);
      const byteArray = new Uint8Array(byteString.length);
      for (let i = 0; i < byteString.length; i++) {
        byteArray[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([byteArray], { type: contentType });

      this.spinnerService.loading = true;
      this.spinnerService.startUpload(name, position);

      try {
        const token = await this.authService.getToken();

        const response = await new Promise<ServerResponse<{ presignFile: IPresignResponse }>>(
          (resolve, reject) => {
            this.http.post<IServerResponse<{ presignFile: IPresignResponse }>>(
              urlApi,
              JSON.stringify({ contentType }),
              {
                headers: {
                  'content-type': 'application/json',
                  'Authorization': token.toString()
                }
              }
            ).subscribe({
              next: (response: IServerResponse<{ presignFile: IPresignResponse }>) => resolve(
                new ServerResponse<{ presignFile: IPresignResponse }>(response)
              ),
              error: (error) => {
                if (error.error) resolve(new ServerResponse(error.error));
                else reject(error);
              },
            });
          }
        );

        if (response.statusCode === 201) {

          flag = true;

          if (response.data?.presignFile?.uploadUrl) {
            await this.awsService.uploadFile(
              response.data.presignFile.uploadUrl,
              blob,
              contentType,
              name,
              position
            );
          }

          this.toastService.show({
            body: `${response.message}`,
            classname: 'bg-success text-white'
          });

        } else {
          this.toastService.show({
            header: 'Error',
            body: `${response.message}`,
            classname: 'bg-danger text-white'
          });
        }

      } catch (error) {
        this.toastService.show({
          header: 'Error',
          body: `${error}`,
          classname: 'bg-danger text-white'
        });
      }

      this.spinnerService.loading = false;
      this.spinnerService.message = '';
      this.spinnerService.clearUpload();

      return flag;

    }

}
